import axiosInstance from 'config/axiosInstance';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { businessMonth, businessRangeQuery, formatMonthDayTime, monthBounds } from 'config/time';
import { MEASUREMENT_CELL, isExceeded } from 'config/measurement';
import { PAGE_SIZE } from 'config/constant';
import React, { useCallback, useEffect, useState } from 'react';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import VideoModal from 'reusable/videopreview';
import { useTranslation } from 'react-i18next';
import EvidenceCell, { EvidenceRow, useEvidenceCheck } from 'reusable/EvidenceCell';
import DeviceTimeZone, { deviceTimeZoneOf } from 'reusable/DeviceTimeZone';
import TableScroll, { PAGE_TITLE_ID, tabId } from 'reusable/TableScroll';
import { pageToRecover } from 'reusable/pageRecovery';
import { useMonthPage } from 'reusable/useMonthPage';


interface DriverAlcoholSubmissionsTableProps {
  id?: string;
  /** A36（2026-11-03 和田様の UAT）。ドライバー詳細の上部の月の切り替えで選んだ月（`YYYY-MM`）。既定は今の月。 */
  month?: string;
  /**
   * 2026-11-04 PM。ドライバー詳細の月の切り替え（DriverView が持つ）。車両の点検履歴と同じく、表の上の道具の行の左に置く。
   */
  monthNavigator?: React.ReactNode;
}

interface DriverAlcoholSubmission extends EvidenceRow {
  user?: {
    name: string;
    phone: string;
  };
  data: string;
  testing_time: string;
  location_name: string;
  // MOB-24 LS-AC5。送った位置をどの時点で取ったか。古い記録・古いアプリは空か項目なし（不明）。
  location_source?: 'measurement' | 'submission' | '';
  memo: string;
}

const DriverAlcoholSubmissionsTable: React.FC<
  DriverAlcoholSubmissionsTableProps
> = ({ id, month = businessMonth(), monthNavigator }) => {
  const { t } = useTranslation('driver');
  // 既定は新しい順（サーバーの並び順）。もう一方は記録の時刻の古い順。
  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.oldest', { ns: 'common' }), value: 'testing_time' },
  ];
  const [currentPage, setCurrentPage] = useMonthPage(month);
  const [sortValue, setSortValue] = useState('');
  const [count, setCount] = useState(0);
  const [driverAlcoholSubmissions, setDriverAlcoholSubmissions] = useState<DriverAlcoholSubmission[]>([]);
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoLink, setCurrentVideoLink] = useState('');

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleVideoClick = useCallback((videoLink: string) => {
    setCurrentVideoLink(videoLink);
    setShowVideo(true);
  }, []);
  const getColorClass = (dataValue: string) => {
    const value = parseFloat(dataValue);
    if (isExceeded(value)) {
      return `${MEASUREMENT_CELL.exceeded} px-2 rounded-md flex items-center justify-center w-24`;
    } else if (value <= 0.0) {
      return `${MEASUREMENT_CELL.clear} px-2 rounded-md flex items-center justify-center w-24`;
    } else {
      return `${MEASUREMENT_CELL.detected} px-2 rounded-md flex items-center justify-center w-24`;
    }
  };

  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      // A36。選んだ月の業務日の範囲。
      const { start, end } = monthBounds(month);
      let apiUrl = '/v1/alcohol_tests/?';
      apiUrl += businessRangeQuery('testing_time', start, end);
      apiUrl += `order_by=${sortValue}&page=${currentPage}&page_size=${PAGE_SIZE}&user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, PAGE_SIZE, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      if (!isCurrent()) return;
      setFetchError(null);
      setDriverAlcoholSubmissions(response.data.results);
      setCount(response.data.count);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, PAGE_SIZE, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch data', error);
      setDriverAlcoholSubmissions([]);
      setFetchError(error);
    }
  }, [id, currentPage, sortValue, month, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeVideo = useCallback(() => setShowVideo(false), []);
  // EV-193・RV-166。未確定の行の「確認して再生」。一覧の行か条件（運転者・ページ・並び・期間）が
  // 変わったら、新しい一覧が届く前でも前の確認の応答は捨て、開いている動画を閉じる。
  const evidenceCheck = useEvidenceCheck(
    handleVideoClick,
    driverAlcoholSubmissions,
    `${id}|${currentPage}|${sortValue}|${month}`,
    closeVideo
  );

  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        {/* ToolBar Section。2026-11-04 PM。左に月の切り替え、右にソート（車両の点検履歴と同じ並び）。 */}
        {monthNavigator ?? <div />}
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
      </div>
      {/* Table */}
      {/* 表（外枠）と読み込みに失敗したときの知らせを1つの div で包む（PR #244 の照合し直しの指摘）。section の space-y-2 の
          子の間の余白は、この div にだけかかる（今までの表の div と同じ）。知らせを section の直接の子にすると、余白を受けて
          見出しの帯の下端から 8px 下がる。 */}
      <div>
      {/* EV-301。狭い幅では表に最小の幅を持たせ、表の外枠の中だけを横に送る。 */}
      <TableScroll labelledBy={`${PAGE_TITLE_ID} ${tabId('alcoholsubmission')}`}>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1">{t('view.alcohol-submission.submit-datetime')}</h6>
          <h6 className="flex-1 text-center">{t('view.alcohol-submission.test-result')}</h6>
          <h6 className="flex-1 text-center">{t('view.alcohol-submission.video-submission')}</h6>
          <h6 className="flex-1 text-center">{t('view.alcohol-submission.last-location')}</h6>
          <h6 className="flex-1 text-center">{t('time-zone.device', { ns: 'common' })}</h6>
        </div>
        <div>
          {showVideo && (
            <VideoModal
              videoLink={currentVideoLink}
              onClose={() => setShowVideo(false)}
            />
          )}
          {driverAlcoholSubmissions.map((submission, index) => (
            <div
              key={index}
              className="grid grid-cols-5 gap-4 px-4 py-3 border-b"
            >
              {/* EV-116。業務の時間帯（会社の時間帯）で出す。2026-11-04 PM。日付と時刻を「日時」の1列に（月日 時分秒）。 */}
              <div className="flex-1 whitespace-nowrap">
                {formatMonthDayTime(submission.testing_time)}
              </div>
              <div className="flex flex-1 items-center justify-center">
                <div className={`${getColorClass(submission.data)}`}>
                  {submission.data} mg/L
                </div>
              </div>
              <EvidenceCell
                submission={submission}
                evidenceCheck={evidenceCheck}
                rowLabel={`${formatMonthDayTime(submission.testing_time)} ID ${submission.id ?? ''}`}
                className="flex-1 border flex items-center rounded-md justify-center"
                iconClassName="mr-2"
              />
              <div className="flex-1 min-w-0 px-4 text-center" data-location-cell>
                <div className="truncate underline text-primary/300">{submission.location_name}</div>
                {/* MOB-24 LS-AC5（PM 判断 2026-10-14）。測定の時点の位置を取れず、送信の時点の位置を送った記録だけ注記する。 */}
                {submission.location_source === 'submission' && (
                  <div className="text-xs text-gray-500 whitespace-normal" data-location-source="submission">
                    {t('view.alcohol-submission.location-at-submission')}
                  </div>
                )}
              </div>
              {/* EV-116 段階3。測定の時刻の時点の端末の時間帯。 */}
              <DeviceTimeZone className="flex-1 text-center" {...deviceTimeZoneOf(submission)} at={submission.testing_time} />
            </div>
          ))}
        </div>
      </TableScroll>
      {/* PR #244 の Codex の指摘。読み込みに失敗したときの知らせは外枠（最小の幅の中身）の外に置き、見える幅の中央に出す。
          失敗のときは行を空にし、知らせは外枠と同じ div の中なので、見出しの帯のすぐ下に出る（縦の位置は直す前と同じ）。 */}
      {fetchError ? <FetchFailed error={fetchError} onRetry={fetchData} /> : null}
      </div>
      {/* Pagination Section */}
      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

export default DriverAlcoholSubmissionsTable;